import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SEO } from "@/components/seo/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { QuestionReview } from "@/components/practice/QuestionReview";
import { MistakeAnalysis } from "@/components/practice/MistakeAnalysis";
import { CheckCircle2, XCircle, Clock, ArrowLeft, RotateCcw } from "lucide-react";

export default function QuizResults() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session");

  const { data: session, isLoading } = useQuery({
    queryKey: ["quiz-session", sessionId],
    enabled: !!sessionId,
    queryFn: async () => {
      const { data: sessionData, error: sessionError } = await supabase
        .from("study_sessions")
        .select(`
          *,
          topics (name, id)
        `)
        .eq("id", sessionId)
        .single();

      if (sessionError) throw sessionError;

      // Fetch attempts for this session
      const { data: attempts, error: attemptsError } = await supabase
        .from("attempts")
        .select(`
          *,
          mcqs (
            id,
            stem,
            option_a,
            option_b,
            option_c,
            option_d,
            option_e,
            correct_option,
            explanation
          )
        `)
        .eq("user_id", sessionData.user_id)
        .gte("attempted_at", sessionData.started_at)
        .lte("attempted_at", sessionData.ended_at || new Date().toISOString());

      if (attemptsError) throw attemptsError;

      const correctCount = attempts?.filter((a) => a.is_correct).length || 0;
      const totalAttempts = attempts?.length || 0;
      const score = totalAttempts > 0 ? Math.round((correctCount / totalAttempts) * 100) : 0;

      return {
        ...sessionData,
        attempts,
        correctCount,
        totalAttempts,
        score,
      };
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
          <p className="text-muted-foreground">Loading results...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">Session not found</p>
          <Button onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const timeTaken = session.total_time_seconds
    ? Math.round(session.total_time_seconds / 60)
    : 0;

  return (
    <div className="container max-w-5xl py-8 space-y-8">
      <SEO
        title="Quiz Results — QuickMedicalReview"
        description="Review your quiz performance and answers"
        path="/quiz-results"
      />

      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold text-foreground">Quiz Results</h1>
        <p className="text-muted-foreground">
          {session.topics?.name || "Practice Session"} — Review your performance
        </p>
      </div>

      {/* Score Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Your Final Score</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center">
            <p className="text-6xl font-bold text-primary mb-2">{session.score}%</p>
            <Progress value={session.score} className="h-3 mb-4" />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-success/10 rounded-lg border border-success/20">
              <CheckCircle2 className="h-8 w-8 text-success mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{session.correctCount}</p>
              <p className="text-sm text-muted-foreground">Correct Answers</p>
            </div>

            <div className="text-center p-4 bg-destructive/10 rounded-lg border border-destructive/20">
              <XCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">
                {session.totalAttempts - session.correctCount}
              </p>
              <p className="text-sm text-muted-foreground">Incorrect Answers</p>
            </div>

            <div className="text-center p-4 bg-primary/10 rounded-lg border border-primary/20">
              <Clock className="h-8 w-8 text-primary mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{timeTaken}</p>
              <p className="text-sm text-muted-foreground">Minutes</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI Mistake Deep-Dive */}
      <MistakeAnalysis attempts={session.attempts || []} topicName={session.topics?.name} />

      {/* Question Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Question Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {session.attempts?.map((attempt: any, index: number) => (
            <QuestionReview
              key={attempt.id}
              questionNumber={index + 1}
              question={attempt.mcqs}
              userAnswer={attempt.is_correct ? attempt.mcqs.correct_option : null}
              isCorrect={attempt.is_correct}
            />
          ))}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-center gap-4">
        <Button size="lg" onClick={() => navigate("/study")}>
          <RotateCcw className="h-4 w-4 mr-2" />
          Take a New Quiz
        </Button>
        <Button size="lg" variant="outline" onClick={() => navigate("/dashboard")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>
    </div>
  );
}
